<template>
  <span :id="model?.system?.id">
    <SmartLinkItemComponent
      :id="model?.system?.id"
      class="content-block"
      :component="!model?.system?.workflow"
    >
      <div class="container">
        <PeopleModal />
      </div>
    </SmartLinkItemComponent>
  </span>
</template>

<script setup lang="ts">
import { type TeamListModel } from '~/models'
import type { PeopleSearchRequest } from '~/types/search-types/PeopleSearchRequest'
import { usePeopleModalStore } from '~/stores/modals/people-modal'
import type { SearchResultPeopleItem } from '~/types/SearchResultItem'
import delay from 'lodash/delay'

const peopleModalStore = usePeopleModalStore()
peopleModalStore.resetStates()
const pagerStore = usePaginationStore()
const route = useRoute()

const props = defineProps<{ model: TeamListModel }>()
peopleModalStore.unMutableLayoutIsFullList =
  firstOrDefault(props.model?.elements.layout.value)?.codename.toLowerCase() === 'full_list'
peopleModalStore.setLayoutIsFullList(peopleModalStore.unMutableLayoutIsFullList)
if (!peopleModalStore.selectedCapability) {
  peopleModalStore.setSelectedCapability(props.model?.elements.relatedCapability.value[0]?.name)
}

async function fetchSelectOptions() {
  await getTaxonomies()
  await getLocations()
}

async function getTaxonomies() {
  const taxonomy = await fetchTaxonomy()
  const positionsTaxonomy = taxonomy?.data
    ?.find((t: any) => t.system.codename === 'title')
    ?.terms.map((t: any) => t.name)
  peopleModalStore.setPositions(positionsTaxonomy || [])

  const capabilitiesTaxonomy = taxonomy?.data
    ?.find((t: any) => t.system.codename === 'capability')
    ?.terms.map((t: any) => t.name)
  peopleModalStore.setCapabilities(capabilitiesTaxonomy || [])

  const lawSchoolTaxonomy = taxonomy?.data
    ?.find((t: any) => t.system.codename === 'law_school')
    ?.terms.map((t: any) => t.name)
  peopleModalStore.setLawSchools(lawSchoolTaxonomy || [])
}

async function getLocations() {
  const locations: any[] = []
  const fetchedLocations = await fetchLocations()
  fetchedLocations?.data?.items.forEach((p: any) =>
    locations.push({
      name: p.elements.title.value,
      value: p.system.codename
    })
  )
  peopleModalStore.setLocations(locations)
}

async function fetchPeoplePages() {
  const body: PeopleSearchRequest = {
    capability: peopleModalStore.selectedCapability,
    location: peopleModalStore.selectedLocation,
    position: peopleModalStore.selectedPosition,
    lawSchool: peopleModalStore.selectedLawSchool,
    searchValue: peopleModalStore.searchValue || '',
    page: pagerStore.currentPage,
    size: peopleModalStore.size,
    sort: peopleModalStore.sortBy
  }
  return await fetchPeopleSearch(body)
}

async function handleDisplayOfItems() {
  peopleModalStore.setLoading(true)
  const data = await fetchPeoplePages()
  peopleModalStore.setItems(data.data?.items as SearchResultPeopleItem[])
  peopleModalStore.setTotalResults(data.data?.totalCount || 0)
  pagerStore.setTotalPages(Math.ceil(peopleModalStore.totalResults / peopleModalStore.size))
  delay(() => peopleModalStore.setLoading(false), 400)
}

function getLocationByQuery(locationQuery: string) {
  if (!locationQuery) return ''
  const queryLocation = peopleModalStore.locations.find(
    (l: any) => l.value.toLowerCase() === locationQuery.toLowerCase()
  )?.value
  return queryLocation ?? ''
}

function getPositionByQuery(positionQuery: string): string {
  if (!positionQuery) return ''
  const queryPosition = peopleModalStore.positions.find(
    (p: any) => p.toLowerCase() === positionQuery.toLowerCase()
  )
  return queryPosition ?? ''
}

function getCapabilitiesByQuery(capabilitiesQuery: string): string {
  if (!capabilitiesQuery) return ''
  const queryCapabilities = peopleModalStore.capabilities.find(
    (c: any) => c.toLowerCase() === capabilitiesQuery.toLowerCase()
  )
  return queryCapabilities ?? ''
}

function getLawSchoolByQuery(lawSchoolQuery: string): string {
  if (!lawSchoolQuery) return ''
  const queryLawSchool = peopleModalStore.lawSchools.find(
    (s: any) => s.toLowerCase() === lawSchoolQuery.toLowerCase()
  )
  return queryLawSchool ?? ''
}

watch(
  () => [
    peopleModalStore.searchValue,
    peopleModalStore.selectedLocation,
    peopleModalStore.selectedPosition,
    peopleModalStore.selectedCapability,
    peopleModalStore.selectedLawSchool,
    peopleModalStore.sortBy,
    peopleModalStore.layoutIsFullList,
    peopleModalStore.openAsModal,
    peopleModalStore.isGrid,
    peopleModalStore.size,
    pagerStore.currentPage
  ],
  async () => {
    await handleDisplayOfItems()
  }
)

await fetchSelectOptions()
//coming from People Search Block
if (route.query.q || route.query.c || route.query.l || route.query.p || route.query.s) {
  peopleModalStore.setSearchValue(route.query.q as string)
  peopleModalStore.setSelectedCapability(getCapabilitiesByQuery(route.query.c as string))
  peopleModalStore.setSelectedLocation(getLocationByQuery(route.query.l as string))
  peopleModalStore.setSelectedPosition(getPositionByQuery(route.query.p as string))
  peopleModalStore.setSelectedLawSchool(getLawSchoolByQuery(route.query.s as string))
}
await handleDisplayOfItems()
</script>
